import { price as priceUtils } from '@eti/utils';
import { useFormAdapter, useFormFields, useTranslation } from '@eti/providers';
import { AirportAvailability } from '@eti/components';
// oxlint-disable-next-line no-restricted-imports -- aligning with sibling products until operation types exist

import { TripType } from '@eti/schema-types';
import ProductLayout from '../../common/main-layout/ProductLayout';
import { useProductData } from '../../common/hook/useProductData';
import { productTexts } from '../../common/utils/getProductTranslations';
import { getUpsellProps } from '../../common/utils/productCardProps';
import { availablityProductCardGrid, availabilityReadMoreGrid } from '../../common/utils/styles';
import { stringToBoolean } from '../../single-choice/utils/selection';
import { PickAndChooseFooter, SegmentsSeedField, buildInitialSelections, deriveCtaState, getActiveSelections, getFromDisplayPrice, getSelectedCount, getTotalDisplayPrice, isSingleSegment, PickAndChooseCtaState, useSegmentDetails } from '../../common/pick-and-choose';
import { getCartAvailability, withAirportNames } from '../../common/utils/availability';
import { LOUNGE_PER_SEGMENT } from '../../products';
import { loungePerSegmentConfig } from '../config';
import LoungeInfoDialog from './LoungeInfoDialog';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  name: PRODUCT_NAME,
  id: PRODUCT_ID
} = LOUNGE_PER_SEGMENT;
const toLabels = (translation, addedPasses) => ({
  selectPass: translation.loungePerSegmentCtaSelectPass,
  editPass: translation.loungePerSegmentCtaEditPass,
  addedPasses,
  zeroPasses: translation.loungePerSegmentCtaZeroPasses,
  drawerTitle: translation.loungePerSegmentDrawerTitle,
  drawerCardTitle: translation.loungePerSegmentDrawerCardTitle,
  totalPriceLabel: translation.loungePerSegmentDrawerTotalPrice,
  save: translation.loungePerSegmentDrawerSave,
  departing: translation.loungePerSegmentAvailabilityCardDeparting,
  return: translation.loungePerSegmentAvailabilityCardReturn,
  seeDetails: translation.loungePerSegmentAvailabilityCardSeeAll,
  validationTitle: translation.loungePerSegmentValidationTitle,
  validationBody: translation.loungePerSegmentValidationBody,
  validationContinueWithout: translation.loungePerSegmentValidationContinueWithout,
  validationKeepEditing: translation.loungePerSegmentValidationKeepEditing
});
const getSegmentAvailabilities = sellSpecification => {
  if ((sellSpecification === null || sellSpecification === void 0 ? void 0 : sellSpecification.__typename) === 'SellSpecificationLoungePerSegmentNew') {
    var _sellSpecification$se;
    return (_sellSpecification$se = sellSpecification.segmentAvailabilities) !== null && _sellSpecification$se !== void 0 ? _sellSpecification$se : [];
  }
  return [];
};
const toAvailabilityList = segments => segments.map(s => ({
  boundIndex: s.customerBoundIndex0Based,
  segmentIndex: s.customerSegmentIndex0Based,
  available: s.available,
  departureAirport: s.departureAirport,
  arrivalAirport: s.arrivalAirport,
  flightNumber: s.flightNumber,
  loungeInfo: s.loungeInfo
}));
const LoungePerSegment = ({
  className,
  form,
  order,
  productCode,
  productPosition,
  sellSpecification,
  layoutType,
  tripType
}) => {
  var _order$bounds, _committed$segments, _committed$value, _order$travelers$leng, _order$travelers, _priceUtils$getPriceD;
  const {
    t
  } = useTranslation();
  const {
    setValue
  } = useFormAdapter();
  const isMultiStop = tripType === TripType.MultiStop;
  const {
    value: committed
  } = useFormFields(`${PRODUCT_NAME}.value`);
  const saved = Boolean(committed === null || committed === void 0 ? void 0 : committed.saved);
  const {
    detailsSegment,
    isDetailsOpen,
    openDetails,
    closeDetails
  } = useSegmentDetails();
  const availabilities = withAirportNames((_order$bounds = order === null || order === void 0 ? void 0 : order.bounds) !== null && _order$bounds !== void 0 ? _order$bounds : [], getSegmentAvailabilities(sellSpecification));
  const baseSegments = buildInitialSelections(availabilities);
  const segments = saved && committed !== null && committed !== void 0 && (_committed$segments = committed.segments) !== null && _committed$segments !== void 0 && _committed$segments.length ? committed.segments : baseSegments;
  const isSelected = Boolean(stringToBoolean((_committed$value = committed === null || committed === void 0 ? void 0 : committed.value) !== null && _committed$value !== void 0 ? _committed$value : false));
  const {
    productData,
    rootElement,
    translation
  } = useProductData({
    className,
    form,
    isSelected,
    layoutType,
    order,
    productId: PRODUCT_ID,
    productName: PRODUCT_NAME,
    productCode,
    productPosition,
    sellSpecification,
    texts: productTexts[PRODUCT_NAME]
  });
  if (!productData || segments.length === 0) {
    return null;
  }
  const single = isSingleSegment(segments);
  const ctaState = deriveCtaState({
    saved,
    segments
  });
  const priceRaw = saved ? getTotalDisplayPrice(segments) : getFromDisplayPrice(segments);
  const openDrawer = airport => {
    const picked = segments.find(s => s.customerBoundIndex0Based === airport.boundIndex && s.customerSegmentIndex0Based === airport.segmentIndex);
    if (!picked) {
      return;
    }
    openDetails(picked);
  };
  const {
    loungePerSegmentAvailabilityCardAddedStatus: addedStatus,
    loungePerSegmentAvailabilityCardAddedTitle: addedTitle,
    loungePerSegmentAvailabilityCardAvailabilityTitle: availabilityTitle,
    loungePerSegmentAvailabilityCardAvailableStatus: availableStatus,
    loungePerSegmentAvailabilityCardDeparting: departingTitle,
    loungePerSegmentAvailabilityCardReturn: returnTitle,
    loungePerSegmentAvailabilityCardSeeAll: seeAll,
    loungePerSegmentAvailabilityCardStatusTooltip: statusTooltip,
    loungePerSegmentAvailabilityCardUnavailableStatus: unavailableStatus,
    loungePerSegmentCartPurchasedMessage: cartPassLabel,
    priceLabelFrom
  } = translation;
  const labels = toLabels(translation, t('loungePerSegmentCtaAddedPasses', getSelectedCount(segments)));
  const numberOfTravelers = (_order$travelers$leng = order === null || order === void 0 || (_order$travelers = order.travelers) === null || _order$travelers === void 0 ? void 0 : _order$travelers.length) !== null && _order$travelers$leng !== void 0 ? _order$travelers$leng : 1;
  const buildCartBreakdown = next => {
    const selectedSegments = getActiveSelections(next);
    if (selectedSegments.length === 0) {
      return null;
    }
    return getCartAvailability(numberOfTravelers, cartPassLabel, selectedSegments.map(s => ({
      available: true,
      departureAirport: s.departureAirport
    })));
  };
  const handleSingleChoiceChange = value => {
    setValue(`${PRODUCT_NAME}.cartBreakDown`, stringToBoolean(value) ? buildCartBreakdown(baseSegments) : null);
  };
  const showFooterPrice = single || ctaState === PickAndChooseCtaState.Select;
  const showPerTraveler = single && !isSelected;
  const displayPriceRaw = showPerTraveler ? (_priceUtils$getPriceD = priceUtils.getPriceDivided(priceRaw, numberOfTravelers)) !== null && _priceUtils$getPriceD !== void 0 ? _priceUtils$getPriceD : priceRaw : priceRaw;
  const availabilityList = toAvailabilityList(segments);
  const {
    upSellObject,
    isUpsellHighlight = false,
    shouldDisplayUpsell = false
  } = getUpsellProps(translation.upSell, {
    shouldDisplayAvailability: availabilityList.length > 0,
    availabilityList,
    tripType
  });
  const updatedProductData = {
    ...productData,
    priceRaw,
    price: showFooterPrice ? priceUtils.formatPrice(displayPriceRaw) : '',
    priceLabel: single ? productData.priceLabel : showFooterPrice ? priceLabelFrom : '',
    ...(showPerTraveler && {
      priceSuffix: t('Products.Price.Label.Per.Person')
    }),
    upSell: upSellObject,
    isHighlighted: isUpsellHighlight,
    shouldDisplayUpsell,
    extraProps: {
      ...productData.extraProps,
      addedStatus,
      addedTitle,
      availabilityList,
      availabilityTitle,
      availableStatus,
      departingTitle,
      onButtonChange: single ? handleSingleChoiceChange : undefined,
      returnTitle,
      seeAll,
      statusTooltip,
      unavailableStatus
    }
  };
  return /*#__PURE__*/_jsxs(ProductLayout, {
    productData: updatedProductData,
    ref: rootElement,
    children: [/*#__PURE__*/_jsx(ProductLayout.ReadMore, {
      customGrid: availabilityReadMoreGrid,
      children: /*#__PURE__*/_jsx(AirportAvailability, {
        isDrawerOpen: isDetailsOpen,
        isMultiStop: isMultiStop,
        openDrawer: openDrawer,
        productData: updatedProductData,
        testid: "lounge-pnc-availability-readmore"
      })
    }), single && isSelected && /*#__PURE__*/_jsx(AirportAvailability, {
      className: availablityProductCardGrid,
      isDrawerOpen: isDetailsOpen,
      isMultiStop: isMultiStop,
      isSelected: isSelected,
      openDrawer: openDrawer,
      productData: updatedProductData,
      testid: "lounge-pnc-availability-purchase"
    }), single ? /*#__PURE__*/_jsx(SegmentsSeedField, {
      availableSegments: baseSegments,
      productName: PRODUCT_NAME
    }) : /*#__PURE__*/_jsx(ProductLayout.Cta, {
      children: /*#__PURE__*/_jsx(PickAndChooseFooter, {
        availableSegments: baseSegments,
        buildCartBreakdown: buildCartBreakdown,
        config: loungePerSegmentConfig,
        isMultiStop: isMultiStop,
        labels: labels
      })
    }), /*#__PURE__*/_jsx(LoungeInfoDialog, {
      isOpen: isDetailsOpen,
      onClose: closeDetails,
      segment: detailsSegment
    })]
  });
};
export default LoungePerSegment;
//# sourceMappingURL=LoungePerSegment.js.map